/**
 * Shared Pre-Chat Form structure — one pages→sections→fields tree for
 * public web survey, in-app, and WhatsApp Flow.
 * Aligned with sophia surveyStructure + survey.md mental model.
 * Keep in sync with backend/app/services/form_definition.py.
 */

export interface ShowIf {
  field_id: string;
  equals: string;
}

export interface FormField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  validation?: Record<string, any>;
  show_if?: ShowIf | null;
  // Forms engine (merged 2026-09-28): multi-rule dependent questions
  // (show / hide / require) and "keep this answer off the contact profile".
  // The builder doesn't edit them yet, but must keep them when saving.
  conditions?: Array<Record<string, any>>;
  save_to_profile?: boolean;
}

export interface FormSection {
  id: string;
  title: string;
  fields: FormField[];
  show_if?: ShowIf | null;
  /** Questions with no section heading on the public form */
  page_body?: boolean;
}

export interface FormPage {
  id: string;
  title?: string;
  sections: FormSection[];
}

export interface LegacySection {
  id: string;
  title: string;
  pages: { id: string; title?: string; fields: FormField[] }[];
  show_if?: ShowIf | null;
}

/** WhatsApp: omitted from Flow JSON */
export const WA_UNSUPPORTED = new Set(['file']);
/** WhatsApp: mapped (e.g. rating → 1–5 choice), not native */
export const WA_LIMITED = new Set(['rating']);

// 2026-09-29 (builder redesign): plainer names; the values are unchanged.
export const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone number' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Choice (pick one)' },
  { value: 'checkbox', label: 'Tick box' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'datetime', label: 'Date & time' },
  { value: 'file', label: 'File upload' },
  { value: 'rating', label: 'Star rating' },
  { value: 'acknowledge', label: 'Agree box' },
  { value: 'note', label: 'Note (text only)' },
] as const;

let idCounter = 0;
export function newLocalId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now()}_${idCounter}`;
}

export function slugifyFieldId(label: string): string {
  const id = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'question';
  // Field IDs must start with a letter (the backend rejects "2nd_visit").
  return /^[a-z]/.test(id) ? id : `q_${id}`;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'form';
}

export function waFlag(type: string): 'ok' | 'limited' | 'unsupported' {
  if (WA_UNSUPPORTED.has(type)) return 'unsupported';
  if (WA_LIMITED.has(type)) return 'limited';
  return 'ok';
}

export function flattenPages(pages: FormPage[]): FormField[] {
  return (pages || []).flatMap((p) =>
    (p.sections || []).flatMap((s) => (Array.isArray(s.fields) ? s.fields : [])),
  );
}

/** A new form: one empty page. 2026-09-29: no forced "About You" section or
 *  Name question any more -- a question with id name / full_name still fills
 *  the contact's name (backend identity rules), and a form without one works. */
export function makeDefaultPages(): FormPage[] {
  return [{ id: 'page_1', title: 'Page 1', sections: [] }];
}

function looksLikePages(data: any): data is FormPage[] {
  return (
    Array.isArray(data)
    && data.length > 0
    && typeof data[0] === 'object'
    && Array.isArray(data[0].sections)
    && !Array.isArray(data[0].pages)
  );
}

function looksLikeLegacySections(data: any): data is LegacySection[] {
  return (
    Array.isArray(data)
    && data.length > 0
    && typeof data[0] === 'object'
    && Array.isArray(data[0].pages)
  );
}

function normalizePages(pages: FormPage[]): FormPage[] {
  return pages.map((p) => ({
    ...p,
    sections: (p.sections || []).map((s) => ({
      ...s,
      fields: Array.isArray(s.fields) ? s.fields : [],
      show_if: s.show_if || null,
      page_body: !!s.page_body,
    })),
  }));
}

export function legacySectionsToPages(sections: LegacySection[]): FormPage[] {
  const pages: FormPage[] = [];
  (sections || []).forEach((section) => {
    (section.pages || []).forEach((page) => {
      pages.push({
        id: page.id || newLocalId('page'),
        title: page.title || undefined,
        sections: [
          {
            id: section.id || newLocalId('section'),
            title: section.title || 'Section',
            fields: Array.isArray(page.fields) ? page.fields : [],
            show_if: section.show_if || null,
          },
        ],
      });
    });
  });
  return pages.length ? pages : makeDefaultPages();
}

/** Normalize any API payload into pages→sections. */
export function pagesFromForm(form: {
  pages?: FormPage[] | null;
  sections?: LegacySection[] | FormPage[] | any[] | null;
  fields?: FormField[] | null;
}): FormPage[] {
  if (looksLikePages(form.pages)) {
    return normalizePages(form.pages);
  }
  if (looksLikePages(form.sections as any)) {
    return normalizePages(form.sections as FormPage[]);
  }
  if (looksLikeLegacySections(form.sections as any)) {
    return legacySectionsToPages(form.sections as LegacySection[]);
  }
  if (Array.isArray(form.fields) && form.fields.length > 0) {
    return [
      {
        id: 'page_legacy_1',
        title: 'Page 1',
        sections: [{ id: 'section_legacy', title: 'Questions', fields: form.fields }],
      },
    ];
  }
  return makeDefaultPages();
}

export function pagesForStorage(pages: FormPage[]): FormPage[] {
  return normalizePages(pages);
}

export function isShowIfMet(showIf: ShowIf | null | undefined, values: Record<string, any>): boolean {
  if (!showIf?.field_id) return true;
  const v = values[showIf.field_id];
  if (Array.isArray(v)) return v.map(String).includes(String(showIf.equals));
  if (showIf.equals === 'true' || showIf.equals === true as any) return v === true || v === 'true';
  return v === showIf.equals || String(v) === String(showIf.equals);
}

export function visibleFieldsOnPage(
  page: FormPage,
  values: Record<string, any>,
): { section: FormSection; fields: FormField[] }[] {
  const out: { section: FormSection; fields: FormField[] }[] = [];
  for (const section of page.sections || []) {
    if (!isShowIfMet(section.show_if, values)) continue;
    const fields = (section.fields || []).filter(
      (f) => f.type !== 'note' && isShowIfMet(f.show_if, values),
    );
    const notes = (section.fields || []).filter(
      (f) => f.type === 'note' && isShowIfMet(f.show_if, values),
    );
    if (fields.length > 0 || notes.length > 0) {
      out.push({ section, fields: [...notes, ...fields] });
    }
  }
  return out;
}

/** Skip pages that have nothing visible given current answers. */
export function visiblePageIndexes(pages: FormPage[], values: Record<string, any>): number[] {
  const idxs: number[] = [];
  (pages || []).forEach((page, i) => {
    if (visibleFieldsOnPage(page, values).length > 0) idxs.push(i);
  });
  return idxs.length ? idxs : [0];
}

export function pageBodySectionId(pageId: string): string {
  return `page_body_${pageId}`;
}

export function ensurePageBodySection(pages: FormPage[], pageId: string): FormPage[] {
  return pages.map((page) => {
    if (page.id !== pageId) return page;
    const existing = (page.sections || []).find((s) => s.page_body || s.id === pageBodySectionId(pageId));
    if (existing) return page;
    return {
      ...page,
      sections: [
        {
          id: pageBodySectionId(pageId),
          title: '',
          fields: [],
          show_if: null,
          page_body: true,
        },
        ...(page.sections || []),
      ],
    };
  });
}

export function findSectionLocation(
  pages: FormPage[],
  sectionId: string,
): { pageId: string; section: FormSection } | null {
  for (const page of pages || []) {
    const section = (page.sections || []).find((s) => s.id === sectionId);
    if (section) return { pageId: page.id, section };
  }
  return null;
}

export function generateFieldId(label: string, pages: FormPage[]): string {
  const existing = flattenPages(pages);
  const base = slugifyFieldId(label);
  let id = base;
  let n = 2;
  while (existing.some((f) => f.id === id)) {
    id = `${base}_${n}`;
    n += 1;
  }
  return id;
}

/** Choice fields that can drive show_if (Pre-Chat: select / checkbox / acknowledge). */
export function dependencyCandidates(pages: FormPage[], beforeFieldId?: string): FormField[] {
  const all = flattenPages(pages);
  const idx = beforeFieldId ? all.findIndex((f) => f.id === beforeFieldId) : all.length;
  const slice = idx >= 0 ? all.slice(0, idx === -1 ? all.length : idx) : all;
  return slice.filter(
    (f) =>
      f.type === 'select'
      || f.type === 'select_one'
      || f.type === 'select_many'
      || f.type === 'checkbox'
      || f.type === 'acknowledge',
  );
}

export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
